import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { fetchPlans, createCheckout } from '../slices/billingSlice';
import { fetchMe } from '../slices/authSlice';
import { HiOutlineCheck, HiOutlineSparkles, HiOutlineLightningBolt, HiOutlineArrowRight } from 'react-icons/hi';

const PLAN_ICONS = {
  free: '🎁',
  starter: '🚀',
  pro: '⚡',
};

const PLAN_FEATURES = {
  free: ['1,000 credits/month', 'Up to 3 scenarios', 'Facebook Lead trigger', 'HTTP requests', 'Basic support'],
  starter: ['10,000 credits/month', 'Unlimited scenarios', 'All integrations', 'Execution history', 'Priority support'],
  pro: ['50,000 credits/month', 'Unlimited scenarios', 'All integrations', 'Advanced analytics', 'Dedicated support', 'Custom webhooks'],
};

export default function Billing() {
  const dispatch = useDispatch();
  const { plans, loading } = useSelector((s) => s.billing);
  const user = useSelector((s) => s.auth.user);
  const [searchParams] = useSearchParams();
  const success = searchParams.get('success');
  const canceled = searchParams.get('canceled');

  useEffect(() => {
    dispatch(fetchPlans());
    if (success) dispatch(fetchMe());
  }, [dispatch, success]);

  const handleUpgrade = (planId) => {
    dispatch(createCheckout(planId)).then((action) => {
      if (action.payload?.url) window.location.href = action.payload.url;
    });
  };

  return (
    <div className="animate-fade-in space-y-8">
      <div>
        <h1 className="page-title">Billing & Plans</h1>
        <p className="page-subtitle">Choose the right plan for your automation needs</p>
      </div>

      {success && (
        <div className="flex items-center gap-3 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl">
          <HiOutlineSparkles className="text-emerald-600 dark:text-emerald-400 flex-shrink-0" size={22} />
          <div>
            <p className="font-semibold text-emerald-800 dark:text-emerald-300">Payment successful!</p>
            <p className="text-sm text-emerald-700 dark:text-emerald-400">Your plan has been upgraded and credits added.</p>
          </div>
        </div>
      )}

      {canceled && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-2xl text-amber-700 dark:text-amber-400 text-sm">
          Payment was canceled. No changes were made.
        </div>
      )}

      {/* Current plan summary */}
      <div className="card p-5">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Current plan</p>
            <p className="text-xl font-bold flex items-center gap-2">
              {PLAN_ICONS[user?.plan]} <span className="capitalize">{user?.plan}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Credits remaining</p>
            <p className="text-xl font-bold">{user?.credits?.toLocaleString()}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-0.5">Resets on</p>
            <p className="text-base font-semibold">{user?.creditsResetDate ? new Date(user.creditsResetDate).toLocaleDateString() : '—'}</p>
          </div>
        </div>
      </div>

      {/* Plan Cards */}
      <div className="grid md:grid-cols-3 gap-5">
        {(plans.length ? plans : [
          { id: 'free', name: 'Free', price: 0, credits: 1000, maxScenarios: 3 },
          { id: 'starter', name: 'Starter', price: 29, credits: 10000, maxScenarios: 'Unlimited' },
          { id: 'pro', name: 'Pro', price: 99, credits: 50000, maxScenarios: 'Unlimited' },
        ]).map((plan) => {
          const isCurrent = user?.plan === plan.id;
          const isPopular = plan.id === 'starter';

          return (
            <div
              key={plan.id}
              className={`relative rounded-2xl border-2 p-6 transition-all duration-200 ${
                isCurrent
                  ? 'border-brand-500 bg-brand-50/50 dark:bg-brand-900/10 shadow-glow'
                  : isPopular
                  ? 'border-purple-400 dark:border-purple-600 bg-white dark:bg-gray-900 shadow-card-hover'
                  : 'border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-card'
              }`}
            >
              {isPopular && !isCurrent && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-gradient-to-r from-brand-500 to-purple-600 text-white text-xs font-bold rounded-full shadow-sm">
                  MOST POPULAR
                </span>
              )}
              {isCurrent && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-brand-600 text-white text-xs font-bold rounded-full shadow-sm">
                  CURRENT PLAN
                </span>
              )}

              <div className="text-3xl mb-3">{PLAN_ICONS[plan.id]}</div>
              <h3 className="text-xl font-bold">{plan.name}</h3>
              <div className="mt-2 mb-6">
                {plan.price === 0 ? (
                  <span className="text-3xl font-bold">Free</span>
                ) : (
                  <>
                    <span className="text-3xl font-bold">${plan.price}</span>
                    <span className="text-gray-500 dark:text-gray-400 text-sm">/month</span>
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-7">
                {(PLAN_FEATURES[plan.id] || []).map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm">
                    <HiOutlineCheck className="text-brand-500 flex-shrink-0 mt-0.5" size={16} />
                    <span className="text-gray-700 dark:text-gray-300">{f}</span>
                  </li>
                ))}
              </ul>

              {plan.id !== 'free' && !isCurrent ? (
                <button
                  onClick={() => handleUpgrade(plan.id)}
                  className={`w-full py-2.5 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
                    isPopular
                      ? 'bg-gradient-to-r from-brand-600 to-purple-600 text-white hover:from-brand-700 hover:to-purple-700 shadow-glow'
                      : 'btn-primary'
                  }`}
                >
                  Upgrade to {plan.name} <HiOutlineArrowRight size={15} />
                </button>
              ) : isCurrent ? (
                <div className="w-full py-2.5 text-center text-sm text-brand-600 dark:text-brand-400 font-semibold bg-brand-50 dark:bg-brand-900/20 rounded-xl">
                  ✓ Your current plan
                </div>
              ) : (
                <div className="w-full py-2.5 text-center text-sm text-gray-400 font-medium rounded-xl bg-gray-50 dark:bg-gray-800">
                  Free forever
                </div>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-gray-400 dark:text-gray-500">
        All plans include secure payment via Stripe. Cancel anytime. Credits don't roll over between billing periods.
      </p>
    </div>
  );
}


// export default function Billing() {
//   const dispatch = useDispatch();
//   const { plans } = useSelector((s) => s.billing);
//   const user = useSelector((s) => s.auth.user);
//   const [searchParams] = useSearchParams();
//   const success = searchParams.get('success');
//   const canceled = searchParams.get('canceled');

//   useEffect(() => {
//     dispatch(fetchPlans());
//     if (success) dispatch(fetchMe());
//   }, [dispatch, success]);

//   const handleUpgrade = (planId) => {
//     dispatch(createCheckout(planId)).then((action) => {
//       if (action.payload?.url) {
//         window.location.href = action.payload.url;
//       }
//     });
//   };

//   return (
//     <div className="animate-fade-in max-w-5xl space-y-6">
//       <div>
//         <h1 className="text-2xl font-bold">Billing</h1>
//         <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">Manage your subscription and plan</p>
//       </div>

//       {success && (
//         <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-400 text-sm flex items-center gap-2">
//           <HiOutlineSparkles size={20} />
//           Payment successful! Your plan has been upgraded.
//         </div>
//       )}

//       {canceled && (
//         <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl text-yellow-700 dark:text-yellow-400 text-sm">
//           Payment was canceled. No changes were made.
//         </div>
//       )}

//       {/* Plan Cards */}
//       <div className="grid md:grid-cols-3 gap-4">
//         {plans.map((plan) => {
//           const isCurrent = user?.plan === plan.id;
//           return (
//             <div
//               key={plan.id}
//               className={`bg-white dark:bg-gray-900 rounded-2xl border-2 p-6 transition-shadow ${
//                 isCurrent
//                   ? 'border-brand-500 shadow-lg shadow-brand-100 dark:shadow-brand-900/20'
//                   : 'border-gray-200 dark:border-gray-800'
//               }`}
//             >
//               {isCurrent && (
//                 <span className="inline-block mb-3 px-2.5 py-0.5 text-xs font-semibold bg-brand-100 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 rounded-full">
//                   Current Plan
//                 </span>
//               )}
//               <h3 className="text-xl font-bold">{plan.name}</h3>
//               <div className="mt-2 mb-5">
//                 <span className="text-3xl font-bold">${plan.price}</span>
//                 <span className="text-gray-500 dark:text-gray-400 text-sm">/month</span>
//               </div>
//               <ul className="space-y-2.5 mb-6">
//                 <li className="flex items-center gap-2 text-sm">
//                   <HiOutlineCheck className="text-brand-500 flex-shrink-0" size={16} />
//                   {plan.credits.toLocaleString()} credits/month
//                 </li>
//                 <li className="flex items-center gap-2 text-sm">
//                   <HiOutlineCheck className="text-brand-500 flex-shrink-0" size={16} />
//                   {plan.maxScenarios === 'Unlimited' ? 'Unlimited' : plan.maxScenarios} scenarios
//                 </li>
//                 <li className="flex items-center gap-2 text-sm">
//                   <HiOutlineCheck className="text-brand-500 flex-shrink-0" size={16} />
//                   {plan.id === 'free' ? 'Basic support' : plan.id === 'starter' ? 'Priority support' : 'Dedicated support'}
//                 </li>
//               </ul>
//               {plan.id !== 'free' && !isCurrent && (
//                 <button
//                   onClick={() => handleUpgrade(plan.id)}
//                   className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white rounded-lg font-medium text-sm transition"
//                 >
//                   Upgrade to {plan.name}
//                 </button>
//               )}
//               {isCurrent && (
//                 <div className="w-full py-2.5 text-center text-sm text-gray-400 font-medium">Active</div>
//               )}
//               {plan.id === 'free' && !isCurrent && (
//                 <div className="w-full py-2.5 text-center text-sm text-gray-400 font-medium">Free tier</div>
//               )}
//             </div>
//           );
//         })}
//       </div>
//     </div>
//   );
// }
